import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, Phone, Star } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { getServiceBySlug, SERVICE_CATALOG } from "@/data/services";
import { CONTACT_INFO, whatsappHref } from "@/constants/contact";

const PROCESS = [
  { step: "01", title: "Inquiry", text: "Share your requirement — role, skills, timeline and budget." },
  { step: "02", title: "Requirement Analysis", text: "We structure the JD, target profile and evaluation criteria." },
  { step: "03", title: "Sourcing", text: "Focused sourcing across our network and premium channels." },
  { step: "04", title: "Screening", text: "Preliminary screening and coordinated interview scheduling." },
  { step: "05", title: "Selection", text: "Offer support, negotiation coordination and closure." },
  { step: "06", title: "Joining", text: "Follow-through until the candidate joins and settles in." },
];

const WHY_US = [
  "Requirement-first, not resume-first",
  "Domain-experienced recruiters",
  "Transparent, honest timelines",
  "Weekly reporting cadence",
  "Assessment & BGV integration",
  "Pan-India delivery",
];

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const svc = getServiceBySlug(slug);
  if (!svc) return <Navigate to="/services" replace />;

  const relatedServices = SERVICE_CATALOG.filter((s) => s.slug !== svc.slug).slice(0, 3);

  return (
    <div className="flex flex-col">
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-navy">
        {/* MOBILE (< lg): stacked — full image on top, text below */}
        <div className="lg:hidden">
          <div className="relative w-full aspect-[4/3] bg-brand-navy overflow-hidden">
            <img
              src={svc.hero}
              alt={svc.title}
              className="absolute inset-0 w-full h-full object-contain"
              style={{ objectPosition: "center center" }}
              loading="eager"
              draggable={false}
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-b from-transparent to-brand-navy" />
          </div>
          <div className="relative bg-brand-navy px-5 py-10 sm:px-6 sm:py-12 overflow-hidden">
            <div className="absolute -top-8 -right-10 w-48 h-48 rounded-full bg-brand-teal/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-8 -left-10 w-48 h-48 rounded-full bg-brand-royal/25 blur-3xl pointer-events-none" />
            <div className="relative z-10">
              <Link to="/services" className="text-white/85 text-sm hover:text-white transition-colors font-medium">
                ← All Services
              </Link>
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/30 text-white text-[11px] font-semibold tracking-[0.22em] uppercase">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" /> Service
              </div>
              <h1 className="mt-4 font-heading font-extrabold text-white text-3xl sm:text-4xl leading-[1.08] tracking-tight">
                {svc.title}
              </h1>
              <p className="mt-4 text-sm sm:text-base text-white/95 leading-relaxed font-medium max-w-lg">
                {svc.tagline}
              </p>
            </div>
          </div>
        </div>

        {/* DESKTOP (lg+): full-bleed image with strong overlay */}
        <div className="hidden lg:block relative">
          <div className="absolute inset-0">
            <img src={svc.hero} alt={svc.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/92 via-brand-navy/80 to-brand-royal/60" />
          </div>
          <div className="container-brand relative z-10 py-28 md:py-32">
            <Link to="/services" className="text-white/85 text-sm hover:text-white transition-colors font-medium">
              ← All Services
            </Link>
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/30 text-white text-[11px] font-semibold tracking-[0.22em] uppercase">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" /> Service
            </div>
            <h1 className="mt-5 font-heading font-extrabold text-white text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
              {svc.title}
            </h1>
            <p className="mt-5 max-w-2xl text-white/95 text-lg leading-relaxed font-medium">
              {svc.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="section-pad bg-white">
        <div className="container-brand grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <span className="eyebrow">Overview</span>
            <h2 className="mt-3 heading-2">What this service covers</h2>
            <p className="mt-5 body-lg">{svc.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact?type=employer" className="btn-primary">
                Discuss This Requirement <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={whatsappHref()} target="_blank" rel="noopener noreferrer" className="btn-secondary">
                <FaWhatsapp className="w-4 h-4" /> Chat on WhatsApp
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <img
              src={svc.image}
              alt={svc.title}
              className="w-full h-[420px] object-cover rounded-2xl border border-brand-border"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="section-pad bg-brand-soft">
        <div className="container-brand">
          <div className="max-w-3xl">
            <span className="eyebrow">Benefits</span>
            <h2 className="mt-3 heading-2">Why teams engage us for {svc.title.toLowerCase()}</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {svc.benefits.map((b) => (
              <div key={b} className="card-surface p-6">
                <div className="p-2 rounded-lg bg-brand-teal/10 text-brand-teal w-fit">
                  <Check className="w-5 h-5" />
                </div>
                <p className="mt-4 text-brand-text font-medium leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section-pad bg-white">
        <div className="container-brand">
          <div className="max-w-3xl">
            <span className="eyebrow">Recruitment Workflow</span>
            <h2 className="mt-3 heading-2">How we deliver this service</h2>
            <p className="mt-4 body-lg">
              A structured six-step process that keeps you informed at every
              stage — with realistic timelines and no over-promises.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROCESS.map((p) => (
              <div key={p.step} className="card-surface p-6">
                <div className="flex items-center gap-3">
                  <span className="w-12 h-12 rounded-xl bg-brand-royal text-white font-heading font-extrabold text-sm flex items-center justify-center">{p.step}</span>
                </div>
                <h3 className="mt-5 font-heading font-bold text-brand-navy text-lg">{p.title}</h3>
                <p className="mt-2 text-sm text-brand-textSoft leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="section-pad bg-brand-soft">
        <div className="container-brand grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="eyebrow">Industries</span>
            <h2 className="mt-3 heading-2">Industries we serve for this practice</h2>
            <p className="mt-4 body-lg">
              We recruit across regulated, product, services and volume
              functions — with recruiter depth in each vertical.
            </p>
          </div>
          <div className="lg:col-span-7 flex flex-wrap gap-3">
            {svc.industries.map((ind) => (
              <span key={ind} className="px-4 py-2 rounded-full bg-white border border-brand-border text-brand-navy font-heading font-semibold text-sm">
                {ind}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="relative bg-brand-royal overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "url(https://images.pexels.com/photos/7580644/pexels-photo-7580644.jpeg?auto=compress&cs=tinysrgb&w=1600)", backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "overlay" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-royal via-brand-royal/95 to-brand-navy/90" />
        <div className="container-brand section-pad relative z-10 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow-light">Why choose us</span>
            <h2 className="mt-3 font-heading font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
              Why partner with Ascend Tech Solutions
            </h2>
            <p className="mt-5 text-white/95 text-base sm:text-lg leading-relaxed font-medium">
              A practical, honest and pan-India recruitment partner — built for teams that value hiring quality over hiring noise.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {WHY_US.map((w) => (
              <div key={w} className="p-5 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-start gap-3">
                <div className="p-1.5 rounded-md bg-brand-teal shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-white" /></div>
                <span className="text-white font-heading font-semibold">{w}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MINI TESTIMONIAL */}
      <section className="section-pad bg-white">
        <div className="container-brand max-w-3xl text-center">
          <div className="flex justify-center gap-1">
            {[...Array(5)].map((_, s) => <Star key={s} className="w-5 h-5 text-amber-500 fill-amber-500" />)}
          </div>
          <p className="mt-6 text-xl md:text-2xl font-heading font-medium text-brand-text leading-snug">
            &ldquo;Ascend Tech Solutions turned a stuck vacancy into a well-fit hire inside three weeks. Communication was straight, timelines were realistic, and the candidate is thriving.&rdquo;
          </p>
          <div className="mt-5 text-sm text-brand-textSoft">— HR Business Partner, Enterprise Services Client</div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-brand-navy">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: `url(${svc.hero})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/95 via-brand-navy/90 to-brand-royal/70" />
        <div className="container-brand section-pad relative z-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <span className="eyebrow-light">Start a conversation</span>
            <h2 className="mt-3 font-heading font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
              Ready to move on {svc.title.toLowerCase()}?
            </h2>
            <p className="mt-4 text-white/95 text-base sm:text-lg font-medium">
              Share your requirement — we&rsquo;ll respond within one business day with next steps and a short discovery call.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <Link to="/contact?type=employer" className="btn-primary bg-brand-teal hover:bg-[#0C8B8E] w-full">
              Send Your Requirement <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={whatsappHref()} target="_blank" rel="noopener noreferrer" className="btn-ghost-light w-full justify-center">
              <FaWhatsapp className="w-4 h-4" /> WhatsApp Us
            </a>
            <a href={CONTACT_INFO.callHrefTel} className="btn-ghost-light w-full justify-center">
              <Phone className="w-4 h-4" /> {CONTACT_INFO.callNumberDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="section-pad bg-brand-soft">
        <div className="container-brand">
          <span className="eyebrow">Related Services</span>
          <h2 className="mt-3 heading-3">Explore other Ascend Tech Solutions services</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {relatedServices.map((r) => (
              <Link key={r.slug} to={`/services/${r.slug}`} className="group card-surface p-6 hover:-translate-y-1 hover:border-brand-royal/40 transition-all">
                <div className="p-2.5 rounded-lg bg-brand-soft text-brand-royal w-fit group-hover:bg-brand-royal group-hover:text-white transition-colors">
                  <r.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-heading font-bold text-brand-navy">{r.title}</h3>
                <p className="mt-1.5 text-sm text-brand-textSoft leading-relaxed">{r.tagline}</p>
                <div className="mt-4 text-xs font-semibold text-brand-royal flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
